import { Alert, Typography } from '@mui/material';
import { getDockerImageDisplayVersion } from 'components/StudiesDashboard/StudySettings/PlatformSettings';
import { Permission } from 'interfaces/permissionOption';
import { includes } from 'lodash';
import React from 'react';
import { usePlatformDockerImages } from 'utils/queryHooks/usePlatformDockerImages';
import { useStudyPlatformVersionSync } from 'utils/queryHooks/useStudyPlatformVersionSync';
import { usePermissions } from 'utils/usePermissions';
import useStudy from 'utils/useStudy';

// Every host of a study job form must call this; enable it only while a version-dependent form is open.
export const useStudyPlatformVersionGate = (studyId: string | undefined, { enabled }: { enabled: boolean }) => {
  const { hasPermission } = usePermissions();
  const { study, isInitialLoading: isLoadingStudy } = useStudy(studyId, { enabled });
  const platformVersionSync = useStudyPlatformVersionSync(studyId, {
    enabled: enabled && hasPermission(Permission.EditStudyNameAndDescription),
  });
  const { images: platformImages, isLoading: isLoadingPlatformImages } = usePlatformDockerImages({
    enabled: enabled && Boolean(studyId),
  });

  const dockerImage = study?.platformSettings?.dockerImage;
  const hasPermissionToEditPlatformSettings = hasPermission(Permission.EditPlatformSettings);
  const showMissingPlatformVersionWarning = enabled && !dockerImage;
  const showUnavailablePlatformVersionWarning =
    enabled &&
    Boolean(dockerImage) &&
    !isLoadingPlatformImages &&
    Array.isArray(platformImages) &&
    !includes(platformImages, dockerImage);

  const platformVersionSyncResult = platformVersionSync.data;
  const platformVersionSyncNotice = platformVersionSync.isError ? (
    <Alert severity="error" sx={{ mb: 2 }}>
      <Typography variant="body2">
        The Platform Version could not be checked for an automatic upgrade. This job will run on the saved Platform
        Version.
      </Typography>
    </Alert>
  ) : platformVersionSyncResult?.updated && platformVersionSyncResult.dockerImage ? (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="body2">
        {`Platform Version was upgraded to ${getDockerImageDisplayVersion(platformVersionSyncResult.dockerImage)}${
          platformVersionSyncResult.previousDockerImage
            ? ` (previously ${getDockerImageDisplayVersion(platformVersionSyncResult.previousDockerImage)})`
            : ''
        }.`}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        This study is set to always use the latest stable Platform Version, so this job will run on the new version.
      </Typography>
    </Alert>
  ) : null;

  const platformVersionStateWarning = showMissingPlatformVersionWarning ? (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography variant="body2">
        Platform Version is not set for this study. This job requires a saved Platform Version in Study Settings before
        it can run.
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {hasPermissionToEditPlatformSettings
          ? 'Set Platform Version in Study Settings, save the study, and then rerun this job.'
          : 'Ask a platform administrator to set Platform Version in Study Settings before running this job.'}
      </Typography>
    </Alert>
  ) : showUnavailablePlatformVersionWarning ? (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography variant="body2">
        The saved Platform Version for this study is no longer available. Choose a current Platform Version in Study
        Settings before running this job.
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {hasPermissionToEditPlatformSettings
          ? 'Update Platform Version in Study Settings, save the study, and then rerun this job.'
          : 'Ask a platform administrator to update Platform Version in Study Settings before running this job.'}
      </Typography>
    </Alert>
  ) : null;

  const platformVersionWarning =
    platformVersionSyncNotice || platformVersionStateWarning ? (
      <>
        {platformVersionSyncNotice}
        {platformVersionStateWarning}
      </>
    ) : null;

  return {
    isLoading: enabled && Boolean(isLoadingStudy || platformVersionSync.isInitialLoading),
    platformVersionWarning,
  };
};
